import type { Agent } from "@/types/agent/agent";
import type { HubConfig } from "@/types/agent/info-links";

/**
 * The public address of a Genie's page. Empty when it has no slug yet.
 *
 * Built off the origin you are actually on, never a hardcoded site URL —
 * otherwise opening a page from localhost throws you onto production, which
 * doesn't have your local routes and answers with a 404.
 */
export const geniePageUrl = (agent: Agent): string | null =>
  agent.url_name
    ? `${window.location.origin}/help/${encodeURIComponent(agent.url_name)}`
    : null;

/** Same-origin path, for the in-app preview iframe. */
export const geniePagePath = (agent: Agent): string | null =>
  agent.url_name ? `/help/${encodeURIComponent(agent.url_name)}` : null;

/** The owner's saved page settings. `{}` for a Genie nobody has configured. */
export const genieHubConfig = (agent: Agent): HubConfig => agent.hub_config ?? {};

/**
 * A Genie Page is live only when it has a slug, the Genie is public, and the
 * owner hasn't switched the page off — those are exactly the three things the
 * public endpoint checks, so anything else would be a link that 404s for the
 * person you sent it to.
 */
export const isPageLive = (agent: Agent): boolean =>
  Boolean(agent.url_name) &&
  (agent.is_public === true || agent.is_marketplace_genie === true) &&
  agent.hub_approved_at !== null &&
  genieHubConfig(agent).enabled !== false;

/** Why the page isn't live, in the owner's words. Null when it is live. */
export const pageBlockedReason = (agent: Agent): string | null => {
  if (!agent.url_name) return "This Genie needs a page address before it can be published.";
  if (agent.is_public !== true && agent.is_marketplace_genie !== true) {
    return "This Genie is private, so its page isn't public. Make the Genie public to publish it.";
  }
  if (genieHubConfig(agent).enabled === false) {
    return "You've switched this page off. Turn it back on to publish it.";
  }
  if (agent.hub_approved_at === null) return "Review and approve your first page content before publishing.";
  return null;
};
